import { useState } from "react";
import type { PublicView } from "../lib/types.ts";
import { Sheet } from "./Sheet.tsx";

/**
 * "Standard rules" or "Custom rules · N changes" (§14.4), with Experimental and Rigged game
 * tags; tap to see the changes. Shown in the lobby, the menu of every phone, the Role tab and
 * Arrival (and, not tappable, on the big screen).
 */
export function RulesBadge({ rules }: { rules: PublicView["rules"] }) {
  const [open, setOpen] = useState(false);
  if (!rules) return null;
  return (
    <>
      <button
        type="button"
        className="btn small ghost"
        onClick={() => setOpen(true)}
        data-testid="rules-badge"
      >
        <span data-testid="rules-badge-text">{rules.badge}</span>
        {rules.experimental ? <span className="tag">Experimental</span> : null}
        {rules.rigged ? <span className="tag">Rigged game</span> : null}
      </button>
      <Sheet open={open} title={rules.badge} onClose={() => setOpen(false)}>
        <div className="stack">
          {rules.experimental ? (
            <p className="muted" style={{ margin: 0 }}>
              <span className="tag">Experimental</span> Some settings are outside the ranges tested
              in playtests.
            </p>
          ) : null}
          {rules.rigged ? (
            <p className="muted" style={{ margin: 0 }}>
              <span className="tag">Rigged game</span> Some roles were forced onto players. The game
              is left out of balance statistics.
            </p>
          ) : null}
          <RuleChanges rules={rules} />
        </div>
      </Sheet>
    </>
  );
}

export function RuleChanges({ rules }: { rules: NonNullable<PublicView["rules"]> }) {
  if (rules.changes.length === 0) {
    return <p className="muted">Every setting is at its default for this setup.</p>;
  }
  return (
    <ul className="list rules-list">
      {rules.changes.map((c) => (
        <li key={c.path}>
          <strong>{c.label}</strong>
          <div className="muted">
            {c.from} → <span style={{ color: "var(--gold-2)" }}>{c.to}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
